import {
  NotificationDtoSchema,
  NotificationListResponseSchema,
  ContadorResponseSchema,
  SnoozeNotificationResponseSchema,
  type NotificationDto,
  type NotificationListResponse,
  type ContadorResponse,
  type SnoozeDelayMode,
  type SnoozeNotificationResponse,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * REST surface for the bell + notification center.
 *
 * Routes are user-scoped (no need to pass userId — server derives from JWT).
 */
export class NotificationsEndpoints {
  constructor(private readonly client: ApiClient) {}

  listar(filter?: { page?: number; limit?: number }): Promise<NotificationListResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (filter?.page !== undefined) query['page'] = filter.page;
    if (filter?.limit !== undefined) query['limit'] = filter.limit;
    return this.client.get('/notifications/me', {
      query,
      responseSchema: NotificationListResponseSchema,
    });
  }

  marcarLida(id: string): Promise<NotificationDto> {
    return this.client.patch(`/notifications/${id}/lida`, undefined, {
      responseSchema: NotificationDtoSchema,
    });
  }

  contador(): Promise<ContadorResponse> {
    return this.client.get('/notifications/me/contador', {
      responseSchema: ContadorResponseSchema,
    });
  }

  /**
   * Phase 47 v1.11 — SNOOZE-INDIV-01. POST /notifications/:id/snooze.
   *
   * Defers a re-dispatch of the notification's push side-effect by one of three
   * presets: `'1h'`, `'4h'`, or `'morning'` (next 08:00 America/Sao_Paulo).
   * Backend computes `scheduledFor` server-side (timezone-correct).
   *
   * Returns `{ notificationId, delayMode, scheduledFor }` — the scheduledFor
   * ISO string drives the green confirmation toast on the mobile UI.
   */
  snooze(id: string, delayMode: SnoozeDelayMode): Promise<SnoozeNotificationResponse> {
    return this.client.post(
      `/notifications/${id}/snooze`,
      { delayMode },
      { responseSchema: SnoozeNotificationResponseSchema },
    );
  }
}
